/**
 * GA4 → B2M event normalization for the GA bridge.
 *
 * Turns whatever an adapter observed (a GA4 measurement-protocol hit, a
 * dataLayer push, or a gtag() call) into the { eventName, params } pair that
 * B2MCustomTracker.track() expects, applying nameMap, eventFilter and the PII
 * deny-list on the way.
 */

/**
 * Event parameters stripped before an event leaves the bridge.
 * GA hits routinely carry raw e-mail / phone / hashed-identity blobs
 * (logo.com.tr's `generate_lead` tag sends ep_email, ep_phone and user_data);
 * forwarding those to a second vendor is a consent problem, so the default is
 * to drop them. Its user_id is base64(phone) and form tags send field_value.
 * Config `piiParams` extends this list; it cannot shrink it.
 */
export const DEFAULT_PII_PARAMS = [
  'email', 'ep_email', 'user_email', 'em',
  'phone', 'ep_phone', 'user_phone', 'ph',
  'user_data', 'address', 'first_name', 'last_name', 'fn', 'ln',
  'password', 'card', 'iban', 'tckn', 'national_id',
  'user_id', 'uid', 'ga_user_id', 'field_value'
];

/**
 * GA4 measurement-protocol keys that are transport plumbing, not event data.
 * Dropped so bridged events don't carry 60 meaningless columns.
 */
const MP_NOISE_KEYS = new Set([
  'v', 'tid', 'gtm', '_p', '_z', '_s', '_eu', '_tu', 'are', 'frm', 'gtm_up',
  'pscdl', 'tag_exp', 'uaa', 'uab', 'uafvl', 'uam', 'uamb', 'uap', 'uapv',
  'uaw', 'gdid', 'ecid', 'gcd', 'dma', 'dma_cps', 'npa', 'rcb', 'ir', 'gaf',
  'tfd', '_fplc', 'ec_mode', 'sst.rnd', 'sst.etld', 'sst.tft', 'sst.lpc',
  'sst.navt', 'sst.ude', 'gap.sstd', 'richsstsse'
]);

/**
 * Named GA4 protocol fields we DO want, under a readable name.
 */
const MP_FIELD_MAP = {
  en: null,                    // event name — handled separately
  cid: 'ga_client_id',
  sid: 'ga_session_id',
  sct: 'ga_session_number',
  seg: 'ga_session_engaged',
  dl: 'page_location',
  dr: 'page_referrer',
  dt: 'page_title',
  ul: 'ga_language',
  sr: 'ga_screen_resolution',
  ur: 'ga_geo_region',
  uid: 'ga_user_id',
  _et: 'engagement_time_msec',
  _fv: 'ga_first_visit',
  _ss: 'ga_session_start',
  _nsi: 'ga_new_session',
  gcs: 'ga_consent_state',
  gclid: 'gclid'
};

/**
 * Consent-Mode string (`gcs=G1<ad><analytics>`) → analytics_storage granted?
 * Returns null when the site is not using Consent Mode.
 * @param {string} gcs
 * @returns {boolean|null}
 */
export function analyticsConsentGranted(gcs) {
  if (typeof gcs !== 'string' || gcs.length < 4 || gcs.slice(0, 2) !== 'G1') return null;
  return gcs.charAt(3) === '1';
}

/**
 * Parse one GA4 measurement-protocol parameter bag into { name, params }.
 * `ep.x` / `epn.x` are event params, `up.x` / `upn.x` user properties.
 * @param {Record<string,string>} bag
 * @returns {{ name: string|null, params: object, userProps: object }}
 */
export function parseMeasurementProtocol(bag) {
  const params = {};
  const userProps = {};
  let name = null;

  Object.keys(bag || {}).forEach((key) => {
    const value = bag[key];
    if (value === undefined || value === '') return;

    if (key === 'en') { name = String(value); return; }
    if (key.indexOf('ep.') === 0) { params[key.slice(3)] = value; return; }
    if (key.indexOf('epn.') === 0) { params[key.slice(4)] = toNumber(value); return; }
    if (key.indexOf('up.') === 0) { userProps[key.slice(3)] = value; return; }
    if (key.indexOf('upn.') === 0) { userProps[key.slice(4)] = toNumber(value); return; }
    if (MP_NOISE_KEYS.has(key)) return;

    const mapped = MP_FIELD_MAP[key];
    if (mapped) params[mapped] = value;
  });

  return { name, params, userProps };
}

/**
 * @param {string} v
 * @returns {number|string} the number when parseable, the raw value otherwise
 */
function toNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : v;
}

/**
 * Build the resolved filter predicate from config.
 * Accepts a function, or an { allow?: string[], deny?: string[] } object.
 * `allow` wins when present: anything not listed is dropped.
 * @param {Function|{allow?:string[],deny?:string[]}|null} eventFilter
 * @returns {(name: string, params: object) => boolean}
 */
export function buildEventFilter(eventFilter) {
  if (typeof eventFilter === 'function') {
    return (name, params) => {
      // A throwing customer filter must not take the host page down.
      try { return eventFilter(name, params) !== false; } catch (e) { return false; }
    };
  }
  if (eventFilter && typeof eventFilter === 'object') {
    const allow = Array.isArray(eventFilter.allow) ? new Set(eventFilter.allow) : null;
    const deny = new Set(Array.isArray(eventFilter.deny) ? eventFilter.deny : []);
    return (name) => {
      if (deny.has(name)) return false;
      if (allow) return allow.has(name);
      return true;
    };
  }
  return () => true;
}

/**
 * Strip PII-bearing parameters in place.
 * @param {object} params
 * @param {Set<string>} denySet lower-cased keys to remove
 */
function scrubPii(params, denySet) {
  Object.keys(params).forEach((key) => {
    if (denySet.has(key.toLowerCase())) delete params[key];
  });
}

/**
 * 32-bit FNV-1a, base36. Keeps the dedupe key short — it is forwarded with
 * every bridged event — while still covering every param value.
 * @param {string} str
 * @returns {string}
 */
function shortHash(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

/**
 * Key used to suppress the same GA event arriving twice (e.g. the dataLayer
 * adapter and the network adapter both running, or GA retrying a hit).
 * @param {string} name
 * @param {object} params
 * @returns {string}
 */
export function dedupeKey(name, params) {
  // An explicit event identity is authoritative: same id → same event.
  const id = params.event_id !== undefined ? ['event_id', params.event_id]
    : params.transaction_id !== undefined ? ['transaction_id', params.transaction_id]
      : null;
  if (id) return name + '|' + id[0] + '=' + id[1];

  const parts = [name];
  ['page_location', 'ga_session_id', 'ga_client_id'].forEach((k) => {
    if (params[k] !== undefined) parts.push(k + '=' + params[k]);
  });
  // Every event param and its value: sites like logo.com.tr carry session-
  // constant params (bq_*) that sort first, so a key built from only a few
  // params merged distinct field_focused / banner impressions on one page.
  const body = [];
  Object.keys(params).sort().forEach((k) => {
    if (k.charAt(0) === '_' || k.indexOf('ga_') === 0 || MP_NOISE_KEYS.has(k)) return;
    body.push(k + '=' + JSON.stringify(params[k]));
  });
  if (body.length) parts.push('p=' + shortHash(body.join('&')));
  return parts.join('|');
}

/**
 * Apply nameMap / eventFilter / PII scrubbing and stamp the bridge markers.
 *
 * @param {object} raw          { name, params, userProps } from an adapter
 * @param {object} options      resolved gaBridge config
 * @param {string} sourceAdapter 'datalayer' | 'gtag' | 'network'
 * @returns {{ eventName: string, params: object }|null} null when filtered out
 */
export function normalizeGaEvent(raw, options, sourceAdapter) {
  if (!raw || !raw.name) return null;

  const nameMap = options.nameMap || {};
  const eventName = Object.prototype.hasOwnProperty.call(nameMap, raw.name)
    ? nameMap[raw.name]
    : raw.name;

  // A nameMap entry of null/'' is an explicit "drop this event".
  if (!eventName) return null;
  if (!options._filter(eventName, raw.params || {})) return null;

  const params = { ...(raw.params || {}) };
  scrubPii(params, options._piiDeny);

  const consent = analyticsConsentGranted(params.ga_consent_state);
  if (consent !== null) params.ga_analytics_consent = consent;

  params.source = 'ga_bridge';
  params.ga_bridge_adapter = sourceAdapter;
  params.ga_event_name = raw.name;
  params.ga_bridge_dedupe_key = dedupeKey(eventName, params);

  return { eventName, params };
}
